import {
  useEffect,
  useState,
} from 'react';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';
import isNil from 'lodash/isNil';
import {
  AllocationResponseBaseDTO,
  CancelledTerminalCheckoutDTO,
  CancelTerminalCheckoutRequest,
  EncounterBillingDataDTO,
  EncounterBillingSummaryResponseDTO,
  PatientBillingSummaryResponseDTO,
  PatientPaymentRequestDTO,
  BatchAllocationResponseDTO,
  AllocationRequestDTO,
  AppointmentPaymentMethodDTO,
  CreatePurchaseRequestDTO,
  EncounterWithCustomCodesViewModel,
  SquareTerminalPaymentMethodDTO,
  OnlinePaymentRequestDTO,
  MoveMoneyFromAppointmentsToCreditRequestDTO,
  None,
  MoveToCreditLogViewModel,
  RefundMethodOptionDTO,
  RefundMethodsResponseDTO,
  PatientPaymentRefundRequestDTO,
} from 'dtos';
import {
  getApiBillingGetAutoAllocationPreviewPatientId,
  getApiBillingGetBillingSummaryDataByEncounterIdEncounterId,
  getApiBillingGetBillingSummaryDataByPatientIdPatientId,
  getApiBillingGetPaymentMethods,
  getApiEncounterGetTodaysEncountersWithCustomCodesByPatientId,
  getApiBillingGetUnpaidEncounterBillingData,
  postApiBillingCreateAllocation,
  postApiBillingCreateAutoAllocationPatientId,
  postApiBillingCreatePatientPayment,
  postApiEncounterCreatePurchase,
  postApiSquarePaymentsCancelTerminalCheckout,
  postApiBillingMovePaymentsFromAppointmentsToCredit,
  getApiPatientGetMoveToCreditLogs,
  getApiBillingGetEncountersWithPositivePayment,
  getApiBillingGetRefundMethods,
  postApiBillingCreatePatientPaymentRefund,
  getApiBillingGetPatientPaymentRefunds,
} from 'endpoints';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';
import {
  PaymentStatus,
} from 'pages/Dashboard/pages/Billing/types';
import {
  paymentsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import useSignalREffect from 'views/EMR/hooks/signalR';
import {
  useNotify,
} from 'core/hooks';
import {
  PurchaseForm,
} from 'views/EMR/Billing/CreatePurchaseDialog';
import {
  normalizeEncounterWithPatientPaymentInfoViewModel,
  normalizeMoveToCreditLogViewModel,
  normalizePatientPaymentRefundViewModel,
  RefundRow,
  serializeOnlinePaymentForm,
  serializePayment,
} from 'views/EMR/Billing/utils';
import {
  useNotifyPaymentUpdate,
  useSendBillForOnlinePayment,
} from 'pages/Dashboard/pages/Billing/hooks';
import {
  PaymentForm,
} from 'views/EMR/Billing/PaymentDialog';
import {
  CreatePurchaseResponseDTO,
} from 'dtos/createPurchaseResponseDTO';
import {
  isNilOrZero,
  roundNumber,
} from '@populateui/utils';
import {
  VisitNoteServiceCode,
} from 'views/EMR/components/ServiceLookup/type';
import {
  EncounterWithPayment,
} from 'views/EMR/Billing/AdjustPayment';
import usePrefetchedData from 'pages/Dashboard/hooks/usePrefetchedData';

type BillingSummaryProps = {
  encounterId?: number;
  patientId?: number;
  isDeposit?: boolean;
};

export type CreatePaymentForm = PurchaseForm | PaymentForm;

export function useSquarePaymentUpdate() {
  const userInfo = useUserInfo();

  useSignalREffect('OnTerminalPaymentUpdated', (status: PaymentStatus) => {
    if (status?.userId === userInfo?.user?.userId) {
      paymentsEventChannel.emit('onSquarePaymentUpdate', status);
    }
  });
}

export function useGetBillingSummary({
  encounterId = 0,
  patientId = 0,
  isDeposit = false,
}: BillingSummaryProps): UseQueryResult<
  Partial<
    EncounterBillingSummaryResponseDTO
    & PatientBillingSummaryResponseDTO
  >, Error> {
  return useQuery<EncounterBillingSummaryResponseDTO, Error>({
    queryKey: ['encounter-billing-summary', encounterId, patientId, isDeposit],
    queryFn: ({ signal }) => (isDeposit
      ? getApiBillingGetBillingSummaryDataByPatientIdPatientId(patientId, { signal })
      : getApiBillingGetBillingSummaryDataByEncounterIdEncounterId(encounterId, { signal })),
    enabled: encounterId > 0 || patientId > 0,
  });
}

export function useGetRefundMethods(): RefundMethodOptionDTO[] {
  const { data } = usePrefetchedData<RefundMethodsResponseDTO>({
    key: ['refund-methods'],
    fetchFn: getApiBillingGetRefundMethods,
  });

  return data?.refundMethods ?? [];
}

export function useGetUnpaidEncounterBillingData(patientId: number): UseQueryResult<
  EncounterBillingDataDTO, Error> {
  return useQuery<EncounterBillingDataDTO, Error>({
    queryKey: ['unpaid-encounters', patientId],
    queryFn: ({ signal }) => getApiBillingGetUnpaidEncounterBillingData({ patientId }, { signal }),
  });
}

function useCancelTerminalPayment() {
  const [cancelling, setCancelling] = useState<boolean>(false);
  const { mutateAsync: cancel } = useMutation<
    CancelledTerminalCheckoutDTO,
    Error,
    CancelTerminalCheckoutRequest
  >({
    mutationFn: (data) => postApiSquarePaymentsCancelTerminalCheckout(data),
  });

  const cancelTerminalPayment = async (checkoutId: string) => {
    setCancelling(true);
    await cancel({ checkoutId });
  };

  return {
    cancelTerminalPayment,
    setCancelling,
    cancelling,
  };
}

function useCreatePurchase() {
  return useMutation< CreatePurchaseResponseDTO, Error, CreatePurchaseRequestDTO >({
    mutationFn: (data) => postApiEncounterCreatePurchase(data),
  });
}

function useCreatePatientPayment() {
  return useMutation< AllocationResponseBaseDTO, Error, PatientPaymentRequestDTO >({
    mutationFn: (data) => postApiBillingCreatePatientPayment(data),
  });
}

export function usePatientPayment() {
  useSquarePaymentUpdate();

  const [checkoutId, setCheckoutId] = useState<string | null>(null);

  const { cancelTerminalPayment, cancelling, setCancelling } = useCancelTerminalPayment();
  const { mutateAsync: collect, isPending: collecting } = useCreatePatientPayment();
  const { mutateAsync: purchase, isPending: isCreatingPurchase } = useCreatePurchase();

  const collectPayment = async (
    data: CreatePaymentForm,
    isPurchase = false,
  ) => {
    const serializedData = serializePayment(data, isPurchase);
    const response = isPurchase
      ? await purchase(serializedData as CreatePurchaseRequestDTO)
      : await collect(serializedData);

    const checkoutId = (response as CreatePurchaseResponseDTO)?.checkoutId;
    if (!isNil(checkoutId)) {
      setCheckoutId(checkoutId ?? null);
    }
    return response;
  };

  const cancelPayment = async () => {
    await cancelTerminalPayment(checkoutId ?? '');
  };

  const reset = () => {
    setCheckoutId(null);
    setCancelling(false);
  };

  return {
    collectPayment,
    cancelPayment,
    reset,
    checkoutId,
    isLoading: collecting || isCreatingPurchase,
    cancelling,
  };
}

export function useGetAutoAllocationPreview(
  patientId: number,
  enabled: boolean,
): UseQueryResult<BatchAllocationResponseDTO, Error> {
  return useQuery<BatchAllocationResponseDTO, Error>({
    queryKey: ['auto-allocation-preview', enabled, patientId],
    queryFn: () => getApiBillingGetAutoAllocationPreviewPatientId(patientId),
    enabled,
  });
}

export function useCreateAllocation(patientId: number, isAuto: boolean): UseMutationResult<
  BatchAllocationResponseDTO, Error, Partial<AllocationRequestDTO>> {
  return useMutation<BatchAllocationResponseDTO, Error, Partial<AllocationRequestDTO>>({
    mutationKey: ['allocation', patientId, isAuto],
    mutationFn: isAuto
      ? () => postApiBillingCreateAutoAllocationPatientId(patientId)
      : (data) => postApiBillingCreateAllocation(data),
  });
}

export function useGetPaymentMethods(): UseQueryResult<AppointmentPaymentMethodDTO[], Error> {
  return useQuery<AppointmentPaymentMethodDTO[], Error>({
    queryKey: ['payment-methods'],
    queryFn: getApiBillingGetPaymentMethods,
  });
}

function calculateDefaultBilledAmountPerUnit(
  customServiceCode: VisitNoteServiceCode,
) {
  const unit = customServiceCode?.numberOfUnits ?? 1;
  const totalBilledAmount = customServiceCode?.customServiceCodeDetails?.billedAmount ?? 0;

  return roundNumber(totalBilledAmount / unit);
}

export function useGetEncountersWithCustomCodesByPatientId(
  patientId: number = 0,
): UseQueryResult<EncounterWithCustomCodesViewModel[], Error> {
  return useQuery<EncounterWithCustomCodesViewModel[], Error>({
    queryKey: ['encountersWithCustomCodes', patientId],
    queryFn: async () => {
      const response = await getApiEncounterGetTodaysEncountersWithCustomCodesByPatientId({
        patientId,
      });
      return response.map((encounter) => ({
        ...encounter,
        customServiceCodes: encounter?.customServiceCodes?.map((customServiceCode) => ({
          ...customServiceCode,
          defaultBilledAmountPerUnit: calculateDefaultBilledAmountPerUnit(customServiceCode),
        })),
      }));
    },
    enabled: !isNilOrZero(patientId),
  });
}

export function useGetPatientEncountersWithPaymentInfo(
  patientId: number,
): UseQueryResult<EncounterWithPayment[], Error> {
  return useQuery<EncounterWithPayment[], Error>({
    queryKey: ['encounterWithPayments', patientId],
    queryFn: async ({ signal }) => {
      const response = await getApiBillingGetEncountersWithPositivePayment({ patientId }, { signal });
      return normalizeEncounterWithPatientPaymentInfoViewModel(response);
    },
    enabled: !isNilOrZero(patientId),
  });
}

export function useGetMoveToCreditLogs(
  patientId: number,
): UseQueryResult<MoveToCreditLogViewModel[], Error> {
  return useQuery<MoveToCreditLogViewModel[], Error>({
    queryKey: ['moveToCreditHistory', patientId],
    queryFn: async () => {
      const response = await getApiPatientGetMoveToCreditLogs({ patientId });
      return normalizeMoveToCreditLogViewModel(response);
    },
    enabled: !isNilOrZero(patientId),
  });
}

export function useGetRefundLogs(
  patientId: number,
): UseQueryResult<RefundRow[], Error> {
  return useQuery<RefundRow[], Error>({
    queryKey: ['refundHistory', patientId],
    queryFn: async () => {
      const response = await getApiBillingGetPatientPaymentRefunds({ patientId });
      return normalizePatientPaymentRefundViewModel(response);
    },
    enabled: !isNilOrZero(patientId),
  });
}

export type AdjustPaymentPayload = MoveMoneyFromAppointmentsToCreditRequestDTO
& PatientPaymentRefundRequestDTO;

export function useAdjustPayments(isMoveToCredit: boolean):
  UseMutationResult<None, Error, AdjustPaymentPayload> {
  return useMutation<None, Error, AdjustPaymentPayload>({
    mutationFn: isMoveToCredit
      ? (data) => postApiBillingMovePaymentsFromAppointmentsToCredit(data)
      : (data) => postApiBillingCreatePatientPaymentRefund(data),
  });
}

export function useTerminalPaymentNotification(
  cancelling: boolean,
  onPaymentComplete: (isComplete: boolean, patientPaymentId?: number) => void,
) {
  const notifyPaymentUpdate = useNotifyPaymentUpdate();
  useEffect(() => {
    const unsubscribe = paymentsEventChannel.on('onSquarePaymentUpdate', ({ isComplete, patientPaymentId }) => {
      notifyPaymentUpdate(isComplete, cancelling);
      onPaymentComplete?.(isComplete ?? false, patientPaymentId);
    });

    return () => { unsubscribe(); };
  }, [cancelling, onPaymentComplete]);
}

export function useSendOnlinePaymentRequest() {
  const { mutateAsync: sendStatement } = useSendBillForOnlinePayment();
  const notify = useNotify();
  const sendOnlinePaymentRequest = async (data: OnlinePaymentRequestDTO, objectLabel: 'Deposit' | 'Payment') => {
    try {
      const { isSuccess } = await sendStatement(
        serializeOnlinePaymentForm(data),
      );
      const message = isSuccess
        ? `Online ${objectLabel} Request was sent to patient successfully!`
        : 'Something went wrong! Please try again.';
      notify({
        message,
        variant: isSuccess ? 'success' : 'warning',
      });
    } catch (error) {
      console.error(error);
    }
  };

  return { sendOnlinePaymentRequest };
}

type ProcessPaymentProps = {
  paymentMethod?: AppointmentPaymentMethodDTO;
  onPaymentComplete?: (patientPaymentId?: number) => void;
};

export function useProcessPayment({
  paymentMethod,
  onPaymentComplete,
}: ProcessPaymentProps) {
  const isTerminal = !isNil((paymentMethod as SquareTerminalPaymentMethodDTO)?.deviceId);

  const {
    collectPayment,
    reset: resetPayment,
    cancelling,
    checkoutId,
    cancelPayment,
  } = usePatientPayment();

  const isCancelable = !isNil(checkoutId);

  const onTerminalPaymentComplete = (isComplete: boolean, patientPaymentId?: number) => {
    if (isComplete) {
      onPaymentComplete?.(patientPaymentId);
    }
    resetPayment();
  };

  useTerminalPaymentNotification(cancelling, onTerminalPaymentComplete);

  const createPayment = async (data: CreatePaymentForm, isPurchase: boolean = false) => {
    try {
      const response = await collectPayment(data, isPurchase);
      if (!isTerminal) {
        onPaymentComplete?.((response as CreatePurchaseResponseDTO)?.patientPaymentId ?? 0);
      }
    } catch (error) {
      console.error(error);
      resetPayment();
    }
  };

  const cancelCurrentPayment = async () => {
    if (isCancelable) {
      await cancelPayment?.();
    }
  };

  return {
    createPayment,
    cancelCurrentPayment,
    cancelling,
    isCancelable,
  };
}
